import dayjs from 'dayjs';
import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import axios, { getAxiosParams } from './lib/axios.config';
import { FlowsApi } from 'typescript-axios';
import { openDatabase, useRunsWaiting } from './lib/utils';

const FLOWS_API = new FlowsApi(getAxiosParams(), undefined, axios);

interface enqueueFormDataPayload {
    flowId: string;
    formData: Record<string, any>;
    files: Array<{ name: string; data: Blob }>;
    previousRunId?: string;
}

interface JobQueueContextData {
    enqueueFormData: (payload: enqueueFormDataPayload) => void;
}

const JobQueueContext = createContext<JobQueueContextData>({
    enqueueFormData: () => { },
});

const putJob = async (job: any) => {
    const db = await openDatabase();
    const transaction = db.transaction('jobQueue', 'readwrite');
    const store = transaction.objectStore('jobQueue');
    store.put(job);
}

const deleteJob = async (job: any) => {
    const db = await openDatabase();
    const transaction = db.transaction('jobQueue', 'readwrite');
    const store = transaction.objectStore('jobQueue');
    store.delete(job.date_created);
}

export const JobQueueProvider = ({ children }: { children: React.ReactNode }) => {
    const { mutate } = useRunsWaiting()
    const isProcessing = useRef(false);

    useEffect(() => {
        processQueue()
        const interval = setInterval(() => {
            processQueue();
        }, 30000); // Check every 30 seconds

        return () => clearInterval(interval);
    }, []);


    const enqueueFormData = async ({ flowId, formData, files, previousRunId }: enqueueFormDataPayload) => {
        const dataToSave = {
            payload: { flowId, formData, files, previousRunId },
            date_created: dayjs.utc().toISOString(),
            attempt: 0,
        };

        const db = await openDatabase();
        const transaction = db.transaction('jobQueue', 'readwrite');
        const store = transaction.objectStore('jobQueue');
        store.add(dataToSave);

        processQueue();

        await new Promise((resolve, reject) => {
            transaction.oncomplete = resolve;
            transaction.onerror = reject;
        });
    };

    const processQueue = async () => {
        if (isProcessing.current || !navigator.onLine) return;
        isProcessing.current = true;

        const db = await openDatabase();
        const transaction = db.transaction('jobQueue', 'readwrite');
        const store = transaction.objectStore('jobQueue');
        const request = store.getAll();

        request.onsuccess = async () => {
            const jobs = request.result.filter(job => !job.isProcessing);
            if (jobs.length === 0) return;

            const job = jobs.reduce((prev, current) => {
                return prev.attempt < current.attempt ? prev : current;
            }, jobs[0]);

            console.debug(`Processing job: ${job.date_created}`);

            // Mark the job as processing
            job.isProcessing = true;
            putJob(job);
            const { payload: { flowId, formData, files, previousRunId }, attempt } = job;

            try {
                // await Promise.reject(new Error('Simulated error')); // Simulate an error for testing
                await FLOWS_API.formSubmitApiV1FlowsFlowIdFormSubmitPost(
                    flowId,
                    JSON.stringify(formData),
                    files,
                    previousRunId
                );
                deleteJob(job);
                mutate()
            } catch (e) {
                console.warn('Error processing queue item:', e);
                const newAttempt = attempt + 1;
                job.attempt = newAttempt;
                job.isProcessing = false;
                if (newAttempt < 20) {
                    putJob(job);
                } else {
                    deleteJob(job);
                    console.error('Max attempts reached. Item removed from queue.');
                }
            }
        };
        isProcessing.current = false;

        await new Promise((resolve, reject) => {
            transaction.oncomplete = resolve;
            transaction.onerror = reject;
        });
    };

    return (
        <JobQueueContext.Provider value={{ enqueueFormData }}>
            {children}
        </JobQueueContext.Provider>
    );
};

export const useJobQueue = () => useContext(JobQueueContext);
